import {ChangeDetectionStrategy, Component} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {AuthService} from "../_services/auth.service";
import {ActivatedRoute, Params, Router, UrlTree} from "@angular/router";
import {SnackbarService} from "../_services/snackbar.service";
import {MatDialog} from "@angular/material/dialog";
import {CustomValidators} from "../_utils/custom-validators";
import {AuthUser} from "../_types/user";
import {TwoFaResponse} from "../_types/admin";
import {ContentService} from "../_services/content.service";
import {DynamicSettings} from "../_types/shared";

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginComponent {
    loginForm: FormGroup;
    requestPasswordForm: FormGroup;
    requestUsernameForm: FormGroup;
    otpForm: FormGroup;
    loading: boolean = false;
    directoryLoading: boolean = false;
    hasEntraQueryParam: boolean = false;
    passwordType: string = 'password';
    view: String = 'Login'
    loginData: AuthUser | undefined;
    timer: number = 0;
    sentTo: TwoFaResponse | undefined;
    returnUrl: string | undefined;
    dynamicSettings: DynamicSettings | undefined;

    constructor(private readonly formBuilder: FormBuilder,
                private authService: AuthService,
                private readonly router: Router,
                private readonly snackbarService: SnackbarService,
                public dialog: MatDialog,
                public contentService: ContentService,
                private readonly route: ActivatedRoute) {
        localStorage.removeItem('lastDynamicCheck')
        localStorage.removeItem('dynamicSettings')
        localStorage.removeItem('userRoles')
        this.dialog.closeAll();
        this.loginForm = this.formBuilder.group({
            username: ['', [Validators.required, Validators.pattern('\\S+')]],
            password: ['', Validators.required],
        });
        this.requestPasswordForm = this.formBuilder.group({
            email: ['', [Validators.required, CustomValidators.email()]],
        });
        this.requestUsernameForm = this.formBuilder.group({
            email: ['', [Validators.required, CustomValidators.email()]],
        });
        this.otpForm = this.formBuilder.group({
            code: ['', [Validators.required]],
        });

        this.authService.getDynamicSettings(true).then((r): void => {
            this.dynamicSettings = r;
            if (this.dynamicSettings.under_construction) {
                this.view = 'construction'
            }
        });

        this.route.queryParams.subscribe(
            (data: Params): void => {
                if (data['username']) {
                    this.loginForm.patchValue({
                        username: data['username']
                    });
                }
                this.hasEntraQueryParam = data['entra'] === '1';
                if (data['ssoError']) {
                    this.snackbarService.openSnackBar(this.ssoErrorMessage(data['ssoError']), 'error');
                }
                if (data['returnUrl']) {
                    this.validateReturnUrl(decodeURIComponent(data['returnUrl']))
                }
            })
    }

    get showAzureSignInButton(): boolean {
        return !!this.dynamicSettings && this.dynamicSettings?.azure_sign_in_enabled &&
            (this.dynamicSettings?.azure_sign_in_button_visible || this.hasEntraQueryParam);
    }

    // The API's Azure SSO callback bounces failed sign-ins back to /login?ssoError=<code>
    ssoErrorMessage(code: string): string {
        switch (code) {
            case 'session':
                return 'We couldn\'t complete your Microsoft sign-in. Please try again using the "Sign in through Microsoft" button.';
            case 'azure':
                return 'Microsoft couldn\'t sign you in. Please try again, or contact your administrator if this keeps happening.';
            case 'account':
                return 'Your account isn\'t currently able to sign in. Please contact your administrator.';
            default:
                return 'Something went wrong during Microsoft sign-in. Please try again.';
        }
    }

    validateReturnUrl(url: string): void {
        const validPathRegex = /^[a-zA-Z0-9\-._~\/?&=]*$/;
        // Check if the URL matches the valid path pattern
        if (url != undefined && url && validPathRegex.test(url) && !url.includes('404')) {
            try {
                const urlTree: UrlTree = this.router.parseUrl(url);
                // If parseUrl doesn't throw an error, the URL is considered valid
                this.returnUrl = url;
            } catch (e) {
                console.warn('Invalid URL:', url);
                // Handle invalid URL scenario
            }
        }
    }

    showPassword(): void {
        if (this.passwordType == 'password') {
            this.passwordType = 'text';
        } else {
            this.passwordType = 'password';
        }
    }

    login(): void {
        this.loading = true;
        this.authService.clearStore();
        this.authService.login(this.loginForm.value.username, this.loginForm.value.password).subscribe({
            next: r => {
                this.loginData = r;
                this.request2fa();
                this.loading = false;
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
                this.loading = false;
            }
        });
    }

    request2fa(): void {
        if (!this.loginData?.token) {
            this.snackbarService.openSnackBar('No login token returned from the server please try again', 'error')
            this.loading = false;
            return;
        }
        this.authService.request2fa(this.loginData.token).subscribe({
            next: r => {
                this.view = 'Code'
                this.sentTo = r;
                this.timer = 25;
                let timeleft: number = 25;
                let that: this = this;
                const downloadTimer: ReturnType<typeof setTimeout> = setInterval(function (): void {
                    if (timeleft <= 0) {
                        clearInterval(downloadTimer);
                    }
                    that.timer = timeleft;
                    timeleft -= 1;
                }, 1000);
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
                this.loading = false;
            }
        });
    }

    finishLogin(): void {
        if (!this.loginData) {
            this.snackbarService.openSnackBar('No login token returned from the server please try again', 'error')
            this.loading = false;
            return;
        }
        this.loading = true;
        this.authService.verify2fa({code: this.otpForm.value.code}, this.loginData.token)
            .subscribe({
                next: r => {
                    if (!this.loginData?.user.role) {
                        this.snackbarService.openSnackBar('Please contact your system administrator to set up your Role and allow you to start using the Virtual Memory Box', 'error')
                    }
                    if (!this.loginData) {
                        this.snackbarService.openSnackBar('No login token returned from the server please try again', 'error')
                        this.loading = false;
                        return;
                    }

                    let sessionToken: string = r.session_token;
                    this.authService.setSessionToken(sessionToken);
                    this.authService.setUserWithToken(this.loginData?.token, false).then((): void => {
                        let validReturnUrl: boolean = !!this.returnUrl && !this.returnUrl.includes('timeline') && !this.returnUrl.includes('people') && !this.returnUrl.includes('404')
                        if (this.loginData?.user.role.role_type == "admin") {
                            if (this.returnUrl && validReturnUrl) {
                                this.router.navigateByUrl(this.returnUrl);
                            } else {
                                this.router.navigate(['/reporting']);
                            }
                        } else {
                            if (this.dynamicSettings?.is_raa && this.loginData?.user.role.role_type == "young_person" && this.loginData?.user.role.name != 'Adult (18+)') {
                                //  Children have no access
                                this.router.navigate(['/noBoxes']);
                            } else if (this.returnUrl && validReturnUrl) {
                                this.router.navigateByUrl(this.returnUrl);
                            } else {
                                this.router.navigate(['/people']);
                            }
                        }
                    }).catch(error => {
                        this.loading = false;
                        this.authService.clearStore();
                        this.view = 'Login';
                        this.snackbarService.openSnackBar(error, 'error');
                        console.error(error);
                    });
                }, error: error => {
                    this.loading = false;
                    this.snackbarService.openSnackBar(error, 'error')
                    console.error(error)
                }
            });
    }

    passwordResetRequest(): void {
        this.loading = true;
        this.authService.passwordResetRequest(this.requestPasswordForm.value.email).subscribe({
            next: r => {
                this.snackbarService.openSnackBar('Check your email for a password reset link.', 'success');
                this.loading = false;
            }, error: error => {
                this.loading = false;
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    usernameRequest(): void {
        this.loading = true;
        this.authService.usernameRequest(this.requestUsernameForm.value.email).subscribe({
            next: r => {
                this.snackbarService.openSnackBar('Check your email for usernames ', 'success');
                this.view = 'Login';
                this.loading = false;
            }, error: error => {
                this.loading = false;
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    azureSignin(): void {
        this.directoryLoading = true;
        this.authService.azureSignIn().subscribe({
            next: r => {
                if (r.redirect_uri) {
                    window.location.href = r.redirect_uri;
                } else {
                    this.snackbarService.openSnackBar('Auth Url error please try again later', 'error')
                }
                this.directoryLoading = false;
            }, error: error => {
                this.directoryLoading = false;
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    loginIfValid(invalid: boolean): void {
        if (!invalid) {
            this.login();
        }
    }

    finishLoginIfValid(invalid: boolean): void {
        if (!invalid) {
            this.finishLogin();
        }
    }

    usernameRequestIfValid(invalid: boolean): void {
        if (!invalid) {
            this.usernameRequest();
        }
    }

    passwordResetRequestIfValid(invalid: boolean): void {
        if (!invalid) {
            this.passwordResetRequest();
        }
    }
}
